import { WSAlert } from "@wingspanhq/fe-component-library";
import { IBulkBatch } from "@wingspanhq/payments/dist/interfaces";
import pluralize from "pluralize";
import { useDownloadSkippedRows } from "../../hooks/useDownloadSkippedRows";
import { useSkippedRowsCount } from "../../hooks/useSkippedRowsCount";
import { BulkResource } from "../../types";

type Props = {
  bulkBatch: IBulkBatch;
  bulkResource: BulkResource;
};

/**
 * Rows that never made it into the batch while uploading the spreadsheet - whether they failed
 * validation or the API refused them - are silently missing from the import. This alert makes them
 * visible and downloadable.
 */
export const BulkBatchUploadUnuploadedRowsAlert: React.FC<Props> = ({
  bulkBatch,
  bulkResource
}) => {
  const { skippedRowsCount, isSkippedRowsCountLoading } = useSkippedRowsCount(
    bulkBatch,
    bulkResource
  );
  const downloadSkippedRows = useDownloadSkippedRows(bulkBatch, bulkResource);

  // nothing says rows were skipped until the live count arrives or, while it loads, the batch label
  if (!skippedRowsCount) {
    return null;
  }

  // the title already says "some rows": the sentence gets the number once it is known, rather than
  // the label's lower number that would be corrected a round-trip later
  const skippedRowsText = isSkippedRowsCountLoading
    ? "Some rows"
    : pluralize("row", skippedRowsCount, true);

  return (
    <WSAlert
      theme="warning"
      icon="alert"
      name="uploadRejections"
      title="Some rows could not be uploaded"
      action={{
        text: "Download skipped rows",
        onClick: downloadSkippedRows
        // onAsyncClick: downloadSkippedRows
      }}
    >
      {skippedRowsText} could not be uploaded and were not imported. You can
      correct these rows and upload them separately.
    </WSAlert>
  );
};
